import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { trackCompleteRegistration } from '../metaPixel';

export default function Register() {
  const { t, i18n }    = useTranslation();
  const { register }   = useAuth();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const examParam = searchParams.get('exam');
  const validExam = examParam && ['qudurat', 'tahsili'].includes(examParam) ? examParam : null;
  const examLabel = validExam ? t(`common.${validExam}`) : null;

  // Where to send the student after a successful signup. Pricing.jsx sends
  // logged-out buyers here with ?redirect=pricing when they click a PAID plan
  // (checkout needs an account first). Without honouring it we drop them into
  // the free trial and silently lose the purchase intent. Allow-listed: never
  // navigate to an arbitrary caller-supplied path.
  const redirectParam = searchParams.get('redirect');
  const redirectTo    = redirectParam === 'pricing' ? 'pricing' : null;

  // C1 acquisition campaign. Sent to the backend, which decides whether a free
  // place is still available — the client never assumes it got one.
  const campaignParam = searchParams.get('campaign');
  const campaign      = campaignParam === 'first100qudurat' ? campaignParam : null;
  const [campaignFull, setCampaignFull] = useState(false);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en');
  };

  // Campaign registrants are NOT on a 7-day trial: they receive a full
  // premium entitlement via the campaign org. Showing the standard trial copy
  // to them contradicts the landing page they just came from, so the headline,
  // subtitle and feature list are all campaign-aware.
  // i18n: defaultValue keeps this working before the campaign.* keys are added
  // to locales/{en,ar}/translation.json. Add them there for the Arabic build.
  const features = campaign
    ? [
        { bold: t('auth.register.campaign.f1_bold', { defaultValue: 'Full access' }),
          rest: t('auth.register.campaign.f1_rest', { defaultValue: ' to every Qudurat World and Level' }) },
        { bold: t('auth.register.campaign.f2_bold', { defaultValue: 'A hint' }),
          rest: t('auth.register.campaign.f2_rest', { defaultValue: ' on every question you get wrong' }) },
        { bold: t('auth.register.campaign.f3_bold', { defaultValue: 'Track your' }),
          rest: t('auth.register.campaign.f3_rest', { defaultValue: ' progress and weakest topics as you go' }) },
      ]
    : [
        { bold: t('auth.register.features.f1_bold'), rest: t('auth.register.features.f1_rest') },
        { bold: t('auth.register.features.f2_bold'), rest: t('auth.register.features.f2_rest') },
        { bold: t('auth.register.features.f3_bold'), rest: t('auth.register.features.f3_rest') },
      ];

  const [form, setForm] = useState({
    username: '', email: '', phone: '', password: '', confirm: '',
  });
  const [error,       setError]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [showPass,    setShowPass]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Client-side phone format check (lenient — backend is the authority).
  // Accepts Saudi (05..., 5..., 966..., +966...) and any international E.164.
  const looksLikeValidPhone = (raw) => {
    const stripped = raw.replace(/[\s\-().]/g, '');
    if (!stripped) return false;
    if (stripped.startsWith('+')) {
      const digits = stripped.slice(1);
      return /^\d{7,15}$/.test(digits);
    }
    if (/^05\d{8}$/.test(stripped))   return true;   // 05XXXXXXXX
    if (/^5\d{8}$/.test(stripped))    return true;   // 5XXXXXXXX
    if (/^966\d{9}$/.test(stripped))  return true;   // 9665XXXXXXXX
    return /^\d{7,15}$/.test(stripped);              // International, no +
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError(t('auth.register.err_password_mismatch')); return; }
    if (form.password.length < 8)       { setError(t('auth.register.err_password_short'));    return; }
    if (!looksLikeValidPhone(form.phone.trim())) {
      setError(t('auth.register.err_phone_format'));
      return;
    }
    setLoading(true);
    try {
      const res = await register(
        form.username.trim(),
        form.email.trim(),
        form.password,
        form.phone.trim(),
        validExam || undefined,
        campaign || undefined,
      );
      // Meta Pixel: signup succeeded → fire the primary B2C conversion event.
      trackCompleteRegistration({ content_name: campaign || validExam || 'free_trial' });

      // The backend is the authority on whether a free place was secured. If
      // the campaign filled between page load and submit, the account is still
      // created — we just tell them honestly instead of promising free access.
      if (campaign && res?.campaign && res.campaign.joined === false) {
        setCampaignFull(true);
        setLoading(false);
        return;
      }
      if (redirectTo === 'pricing') {
        // Back to the plan they were buying, not into the free trial.
        navigate(validExam ? `/pricing?exam=${validExam}` : '/pricing', { replace: true });
      } else if (validExam) {
        navigate(`/exam/${validExam}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err?.error?.message || t('auth.register.err_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{ alignItems: 'flex-start', paddingTop: 48 }}>
      <div className="auth-card" style={{ maxWidth: 460, position: 'relative' }}>

        {/* Language toggle pill — top-right (top-left in RTL via auto flip) */}
        <button
          className="lang-toggle"
          onClick={toggleLang}
          aria-label={t('auth.lang_toggle_aria')}
          style={{ position: 'absolute', top: 16, insetInlineEnd: 16 }}
        >
          <span className={i18n.language === 'en' ? 'lang-active' : ''}>EN</span>
          <span className="lang-sep" />
          <span className={i18n.language === 'ar' ? 'lang-active' : ''}>AR</span>
        </button>

        {/* Logo — no home link; registration is a conversion dead-end */}
        <div className="auth-logo" style={{ marginBottom: 20 }}>
          <span className="logo-dr">Dr</span><span className="logo-fahm">Fahm</span>
        </div>

        {/* Headline */}
        <h1 style={{
          fontSize: '1.75rem', fontWeight: 800, textAlign: 'center',
          color: 'var(--brand-navy)', marginBottom: 6, lineHeight: 1.2,
        }}>
          {campaign
            ? t('auth.register.campaign.title', { defaultValue: 'Claim your free Qudurat access' })
            : examLabel
              ? t('auth.register.title_with_exam', { exam: examLabel })
              : t('auth.register.title_no_exam')}
        </h1>
        <p style={{
          textAlign: 'center', color: 'var(--text-secondary)',
          fontSize: '0.9rem', marginBottom: 20,
        }}>
          {campaign
            ? t('auth.register.campaign.subtitle', { defaultValue: 'One of the first 100 places. No payment, no card.' })
            : t('auth.register.subtitle')}
        </p>

        {/* Feature checklist */}
        <div style={{
          background: 'var(--bg-card-2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '14px 18px', marginBottom: 24,
        }}>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
              <span style={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(21,128,61,0.1)', border: '1.5px solid rgba(21,128,61,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#15803D', fontSize: '0.7rem', fontWeight: 800,
              }}>✓</span>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--brand-navy)' }}>{f.bold}</strong>{f.rest}
              </span>
            </div>
          ))}
        </div>

        {campaignFull && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            <strong>The 100 free places have just been taken.</strong> Your account was created and
            you can start your free trial now — you can upgrade any time from the pricing page.
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => navigate(validExam ? `/exam/${validExam}` : '/dashboard', { replace: true })}
                style={{
                  padding: '9px 16px', background: '#15803D', color: 'var(--brand-sand)',
                  border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'Tajawal, sans-serif',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

        <form style={{ display: 'flex', flexDirection: 'column', gap: 14 }} onSubmit={handleSubmit}>

          <div className="form-group">
            <label className="form-label">{t('auth.register.username_label')}</label>
            <input
              className="form-input" type="text"
              placeholder={t('auth.register.username_placeholder')}
              value={form.username} onChange={set('username')}
              autoComplete="username" minLength={3} maxLength={80} required
            />
          </div>

          {/* ── Email — required ── */}
          <div className="form-group">
            <label className="form-label">
              {t('auth.register.email_label')}{' '}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85rem' }}>
                {t('auth.register.email_hint')}
              </span>
            </label>
            <input
              className="form-input" type="email"
              placeholder={t('auth.register.email_placeholder')}
              value={form.email} onChange={set('email')}
              autoComplete="email" required
            />
          </div>

          {/* ── Phone — required, Saudi format preferred ── */}
          <div className="form-group">
            <label className="form-label">
              {t('auth.register.phone_label')}{' '}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85rem' }}>
                {t('auth.register.phone_hint')}
              </span>
            </label>
            <input
              className="form-input" type="tel"
              placeholder={t('auth.register.phone_placeholder')}
              value={form.phone} onChange={set('phone')}
              autoComplete="tel"
              dir="ltr"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('auth.register.password_label')}</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={showPass ? 'text' : 'password'}
                placeholder={t('auth.register.password_placeholder')}
                value={form.password} onChange={set('password')}
                autoComplete="new-password" minLength={8} required
                style={{ paddingInlineEnd: 64 }}
              />
              <button
                type="button" onClick={() => setShowPass((v) => !v)}
                style={{
                  position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600,
                  fontFamily: 'Tajawal, sans-serif',
                }}
              >
                {showPass ? t('auth.register.hide') : t('auth.register.show')}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('auth.register.confirm_label')}</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={showConfirm ? 'text' : 'password'}
                placeholder={t('auth.register.confirm_placeholder')}
                value={form.confirm} onChange={set('confirm')}
                autoComplete="new-password" required
                style={{ paddingInlineEnd: 64 }}
              />
              <button
                type="button" onClick={() => setShowConfirm((v) => !v)}
                style={{
                  position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600,
                  fontFamily: 'Tajawal, sans-serif',
                }}
              >
                {showConfirm ? t('auth.register.hide') : t('auth.register.show')}
              </button>
            </div>
          </div>

          <button
            type="submit" disabled={loading}
            style={{
              width: '100%', padding: '13px 20px',
              background: loading ? 'rgba(21,128,61,0.6)' : '#15803D',
              color: 'var(--brand-sand)',
              border: 'none', borderRadius: 8,
              fontSize: '1rem', fontWeight: 800,
              fontFamily: 'Tajawal, sans-serif',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.16s ease',
              marginTop: 4,
            }}
            onMouseEnter={(e) => { if (!loading) e.target.style.background = '#166534'; }}
            onMouseLeave={(e) => { if (!loading) e.target.style.background = '#15803D'; }}
          >
            {loading
              ? t('auth.register.submitting')
              : campaign
                ? t('auth.register.campaign.submit', { defaultValue: 'Claim my free access' })
                : t('auth.register.submit')}
          </button>
        </form>

        <div style={{
          display: 'flex', justifyContent: 'center', gap: 20,
          marginTop: 14, flexWrap: 'wrap',
        }}>
          {(campaign
            ? [
                t('auth.register.campaign.trust_1', { defaultValue: 'No payment, no card' }),
                t('auth.register.campaign.trust_2', { defaultValue: 'Full Qudurat access' }),
                t('auth.register.campaign.trust_3', { defaultValue: 'Takes about a minute' }),
              ]
            : [
                t('auth.register.trust_1'),
                t('auth.register.trust_2'),
                t('auth.register.trust_3'),
              ]
          ).map((tx) => (
            <span key={tx} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: '0.78rem', color: 'var(--text-muted)',
            }}>
              <span style={{ color: '#15803D', fontWeight: 700, fontSize: '0.75rem' }}>✓</span>
              {tx}
            </span>
          ))}
        </div>

        <div style={{
          borderTop: '1px solid var(--border)', marginTop: 16,
          paddingTop: 14, textAlign: 'center',
        }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 10 }}>
            {t('auth.register.terms_prompt')}{' '}
            <Link to="/terms" style={{ color: '#15803D', textDecoration: 'none' }}>
              {t('auth.register.terms_link')}
            </Link>.
          </p>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {t('auth.register.switch_prompt')}{' '}
            <Link to="/login" style={{ color: '#15803D', fontWeight: 700, textDecoration: 'none' }}>
              {t('auth.register.switch_link')}
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}